import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslocoService } from '@jsverse/transloco';
import { resolveRouteLabel } from '../utils/route-announcement';

/**
 * RESPONSIBILITY: Announce SPA route changes to screen readers, in the
 * user's language.
 *
 * Angular swaps the routed component without a full page load, so
 * assistive tech hears nothing on navigation (WCAG 4.1.3 Status
 * Messages). This service pipes every NavigationEnd through the CDK
 * LiveAnnouncer's polite `aria-live` region with a localised
 * "Navigated to {page}" message — Japanese users hear
 * 「ホームに移動しました」, not an English string.
 *
 * Page names resolve through the navigation registry's translation keys (the same keys
 * the bottom nav uses) so announcements always match the visible nav
 * labels — see core/utils/route-announcement.ts. Unmapped URL segments
 * fall back to a humanised segment.
 *
 * SIGNALS: none — event-driven, fire-and-forget.
 *
 * USED BY: AppShellComponent (calls start() once at boot). Bare routes
 * (signage, vendor portal, the previews) never start it, so public
 * kiosk surfaces stay silent.
 */
@Injectable({ providedIn: 'root' })
export class RouteAnnouncerService {
  private readonly router = inject(Router);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly transloco = inject(TranslocoService);

  private started = false;
  private lastAnnouncedPath: string | null = null;

  /** Begin announcing navigations. Idempotent; lives for the app session. */
  start(): void {
    if (this.started) return;
    this.started = true;

    // Seed with the boot URL so the initial load is NOT announced — the
    // document title + first-render reading cover it; announcing would
    // double up for screen-reader users.
    this.lastAnnouncedPath = this.router.url.split('?')[0];

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe({
        next: (e) => {
          // Dedupe on path so query-only changes (theme toggles, schedule
          // ?type= tab switches) don't spam the live region — in-page state
          // changes announce themselves via aria-selected / aria-live where
          // relevant.
          const path = e.urlAfterRedirects.split('?')[0];
          if (path === this.lastAnnouncedPath) return;
          this.lastAnnouncedPath = path;

          const { labelKey, fallback } = resolveRouteLabel(e.urlAfterRedirects);
          const page = labelKey ? this.transloco.translate(labelKey) : fallback;
          const message = this.transloco.translate('shell.a11y.navigated_to', { page });
          void this.liveAnnouncer.announce(message, 'polite');
        },
        // Router event streams don't error in practice; guard anyway so a
        // hypothetical failure can never break announcements or bubble to
        // the global handler (mirrors app.ts's router subscription).
        error: () => {},
      });
  }
}
